import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Flex_Items() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🧩 Flex Item Properties</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Flex items are the children of a flex container. Each item can have
        properties that control its growth, shrinkage, basis, alignment, and
        order within the container. This allows for highly flexible and
        responsive designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>flex-grow</code>: defines how much an item will grow relative
            to others.
          </li>
          <li>
            <code>flex-shrink</code>: defines how much an item will shrink
            relative to others.
          </li>
          <li>
            <code>flex-basis</code>: initial main size of the item before
            growing or shrinking.
          </li>
          <li>
            <code>align-self</code>: overrides <code>align-items</code> for a
            single item.
          </li>
          <li>
            <code>order</code>: changes the visual order of items without
            changing HTML structure.
          </li>
        </ul>
      </div>

      {/* Example 1: Flex Grow */}
      <h2 className="section-title">1️⃣ Flex Grow</h2>
      <CodeCopyBlock
        code={`.flex-container {
  display: flex;
  gap: 10px;
}
.item1 { flex-grow: 1; background: #3b82f6; }
.item2 { flex-grow: 2; background: #2563eb; }
.item3 { flex-grow: 1; background: #60a5fa; }`}
      />
      <div style={{ display: "flex", gap: "10px", marginBottom: "2rem" }}>
        <div
          style={{
            flexGrow: 1,
            background: "#3b82f6",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          1
        </div>
        <div
          style={{
            flexGrow: 2,
            background: "#2563eb",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          2
        </div>
        <div
          style={{
            flexGrow: 1,
            background: "#60a5fa",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          3
        </div>
      </div>

      {/* Example 2: Flex Shrink */}
      <h2 className="section-title">2️⃣ Flex Shrink</h2>
      <CodeCopyBlock
        code={`.flex-container { display: flex; width: 400px; gap: 10px; }
.item1 { flex-shrink: 1; background: #f97316; }
.item2 { flex-shrink: 3; background: #ea580c; }
.item3 { flex-shrink: 1; background: #fbbf24; }`}
      />
      <div
        style={{
          display: "flex",
          width: "400px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            flexShrink: 1,
            background: "#f97316",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          1
        </div>
        <div
          style={{
            flexShrink: 3,
            background: "#ea580c",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          2
        </div>
        <div
          style={{
            flexShrink: 1,
            background: "#fbbf24",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          3
        </div>
      </div>

      {/* Example 3: Flex Basis */}
      <h2 className="section-title">3️⃣ Flex Basis</h2>
      <CodeCopyBlock
        code={`.flex-container { display: flex; gap: 10px; }
.item1 { flex-basis: 100px; background: #10b981; }
.item2 { flex-basis: 200px; background: #059669; }
.item3 { flex-basis: 150px; background: #047857; }`}
      />
      <div style={{ display: "flex", gap: "10px", marginBottom: "2rem" }}>
        <div
          style={{
            flexBasis: "100px",
            background: "#10b981",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          1
        </div>
        <div
          style={{
            flexBasis: "200px",
            background: "#059669",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          2
        </div>
        <div
          style={{
            flexBasis: "150px",
            background: "#047857",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          3
        </div>
      </div>

      {/* Example 4: Align Self */}
      <h2 className="section-title">4️⃣ Align Self</h2>
      <CodeCopyBlock
        code={`.flex-container { display: flex; align-items: center; gap: 10px; height: 150px; }
.item1 { align-self: flex-start; background: #3b82f6; }
.item2 { align-self: center; background: #2563eb; }
.item3 { align-self: flex-end; background: #60a5fa; }`}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          height: "150px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            alignSelf: "flex-start",
            background: "#3b82f6",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          1
        </div>
        <div
          style={{
            alignSelf: "center",
            background: "#2563eb",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          2
        </div>
        <div
          style={{
            alignSelf: "flex-end",
            background: "#60a5fa",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          3
        </div>
      </div>

      {/* Example 5: Order */}
      <h2 className="section-title">5️⃣ Order</h2>
      <CodeCopyBlock
        code={`.flex-container { display: flex; gap: 10px; }
.item1 { order: 3; background: #f43f5e; }
.item2 { order: 1; background: #f97316; }
.item3 { order: 2; background: #fbbf24; }`}
      />
      <div style={{ display: "flex", gap: "10px", marginBottom: "2rem" }}>
        <div
          style={{
            order: 3,
            background: "#f43f5e",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          1
        </div>
        <div
          style={{
            order: 1,
            background: "#f97316",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          2
        </div>
        <div
          style={{
            order: 2,
            background: "#fbbf24",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          3
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine <code>flex-grow</code>, <code>flex-shrink</code>, and{" "}
            <code>flex-basis</code> for responsive item sizing.
          </li>
          <li>
            Use <code>align-self</code> to adjust individual item alignment
            within the container.
          </li>
          <li>
            Experiment with <code>order</code> to create dynamic layouts without
            changing HTML.
          </li>
          <li>
            Combine multiple flex containers and items for complex UI
            components.
          </li>
          <li>
            Try responsive designs with <strong>media queries</strong> and flex
            properties.
          </li>
        </ul>
      </div>
    </div>
  );
}
